import { useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

type Category = "bills" | "goals" | "income" | "budget_reset";

const TABLE_TO_CATEGORY: Record<string, Category> = {
  expenses: "bills",
  goals: "goals",
  income_sources: "income",
  budgets: "budget_reset",
};

/**
 * Auto-pushes Fennest changes to Google Calendar.
 *
 * Subscribes to realtime postgres_changes on the four source tables. Each
 * change tags a single category as dirty; the debounced (5s) sync then pushes
 * only the dirty categories via `google-calendar-sync` with `{ categories }`.
 * The edge function skips rows whose `updated_at <= last_sync_at`, making the
 * background sync incremental. Manual "Sync now" and force resync still run
 * full-scope syncs.
 *
 * No-ops when the user has no calendar connection row.
 */
export function useCalendarAutoSync() {
  const { user } = useAuth();
  const enabledRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inFlightRef = useRef(false);
  const pendingRef = useRef(false);
  const lastSyncAtRef = useRef(0);
  const dirtyCategoriesRef = useRef<Set<Category>>(new Set());

  useEffect(() => {
    if (!user) return;
    let cancelled = false;

    const runQueuedSync = async () => {
      if (!enabledRef.current || cancelled) return;
      if (inFlightRef.current) {
        pendingRef.current = true;
        return;
      }

      const elapsed = Date.now() - lastSyncAtRef.current;
      if (elapsed < 20_000) {
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(runQueuedSync, 20_000 - elapsed);
        return;
      }

      // Snapshot and clear the dirty set before invoking so new changes during
      // the sync queue a follow-up run instead of being lost.
      const categories = Array.from(dirtyCategoriesRef.current);
      dirtyCategoriesRef.current = new Set();

      inFlightRef.current = true;
      pendingRef.current = false;
      try {
        await supabase.functions.invoke("google-calendar-sync", {
          body: categories.length ? { categories } : {},
        });
        lastSyncAtRef.current = Date.now();
      } catch { /* silent */ }
      finally {
        inFlightRef.current = false;
        if ((pendingRef.current || dirtyCategoriesRef.current.size > 0) && !cancelled) {
          pendingRef.current = false;
          timerRef.current = setTimeout(runQueuedSync, 5000);
        }
      }
    };

    // Decide whether to enable based on the presence of a connection row.
    (async () => {
      const { data } = await supabase
        .from("google_calendar_connections" as any)
        .select("user_id")
        .eq("user_id", user.id)
        .maybeSingle();
      if (!cancelled) enabledRef.current = !!data;
    })();

    const scheduleForTable = (table: string) => {
      if (!enabledRef.current) return;
      const cat = TABLE_TO_CATEGORY[table];
      if (cat) dirtyCategoriesRef.current.add(cat);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(runQueuedSync, 5000);
    };

    const channel = supabase
      .channel(`gcal-auto-sync-${user.id}`)
      .on("postgres_changes",
        { event: "*", schema: "public", table: "expenses", filter: `user_id=eq.${user.id}` },
        () => scheduleForTable("expenses"))
      .on("postgres_changes",
        { event: "*", schema: "public", table: "goals", filter: `user_id=eq.${user.id}` },
        () => scheduleForTable("goals"))
      .on("postgres_changes",
        { event: "*", schema: "public", table: "income_sources", filter: `user_id=eq.${user.id}` },
        () => scheduleForTable("income_sources"))
      .on("postgres_changes",
        { event: "*", schema: "public", table: "budgets", filter: `user_id=eq.${user.id}` },
        () => scheduleForTable("budgets"))
      // Connection row may appear/disappear during the session.
      .on("postgres_changes",
        { event: "*", schema: "public", table: "google_calendar_connections", filter: `user_id=eq.${user.id}` },
        (payload) => {
          enabledRef.current = payload.eventType !== "DELETE";
        })
      .subscribe();

    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
      supabase.removeChannel(channel);
    };
  }, [user]);
}
